import { twMerge } from '@shared/themes';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { UpArrowButton } from '../../UpArrowButton/UpArrowButton';

type StaticLayoutProps = {
  children: ReactNode;
  id?: string;
  className?: string;
  'data-testid'?: string;
};

/**
 * Page shell for JSON-driven legal and information pages.
 *
 * Provides the main landmark, constrained content width, skip-link target, and
 * back navigation. Intended as the layout wrapper around static page content
 * rather than being composed inside individual pages.
 *
 * @param children - Page body rendered inside the inner content column
 * @param id - Landmark id for skip links; defaults to `main-content`
 * @param className - Optional class names merged onto the main landmark
 */
function StaticLayout({ children, id = 'main-content', className, 'data-testid': dataTestId = 'static-layout' }: StaticLayoutProps) {
  const t = useTranslations('general');

  return (
    <main
      id={id}
      className={twMerge('py-5 md:py-12 ll-grid-container [--ll-container-max-width:728px] [&_hr]:border-t-2 [&_hr]:border-border [&_a]:whitespace-normal', className)}
      tabIndex={-1}
      data-testid={dataTestId}
    >
      <UpArrowButton className="ll-container-inner" ariaLabel={t('static_layout_arial_label')} />
      <div className="ll-container-inner pt-7 pb-5 md:pb-10">{children}</div>
    </main>
  );
}

export { StaticLayout, type StaticLayoutProps };
